import Breadcrumbs from "@material-ui/core/Breadcrumbs";
import Link from "@material-ui/core/Link";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ArrowRight from "@material-ui/icons/KeyboardArrowRightRounded";
import React, { ComponentProps, ComponentType, ReactElement, useMemo } from "react";
import { Color, SvgIconSize } from "../../theme";
import { HasTestId } from "../../types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BreadcrumbElementType = ComponentType<any>;

/**
 * The real structure of a breadcrumb item which is abstracted by `Breadcrumb` in order to provide proper type safety.
 *
 * @see Breadcrumb
 */
export interface BreadcrumbItem<LinkT extends BreadcrumbElementType> extends HasTestId {
    label: string;
    /**
     * Element type to be used as link and filled with `label` as `children` and `href`.
     * Defaults to standard Material UI [`Link`](https://material-ui.com/api/link/).
     */
    Link: LinkT;
    /**
     * Further properties forwarded to used `Link`.
     */
    LinkProps: Omit<ComponentProps<LinkT>, "children">;
}

/**
 * An abstracted breadcrumb which is required for proper type safety. However, it can easily be created by provided `breadcrumb*` methods.
 *
 * @see breadcrumb
 * @see breadcrumb
 * @see breadcrumbLink
 */
export type Breadcrumb = <ResultT extends unknown>(breadcrumbCreator: <LinkT extends BreadcrumbElementType>(breadcrumbItem: BreadcrumbItem<LinkT>) => ResultT) => ResultT;

export interface BreadcrumbNavigationProps extends HasTestId {
    /**
     * A list of breadcrumbs. Every breadcrumb itself can easily be by the exported `breadcrumb*` methods.
     *
     * @see Breadcrumb
     */
    breadcrumbs: Breadcrumb[];
}

export const BreadcrumbNavigation = ({ breadcrumbs, testId }: BreadcrumbNavigationProps): ReactElement => {
    const classes = useStyles();

    const separator = useMemo(() => <ArrowRight style={{ fontSize: SvgIconSize.small }} />, []);

    return (
        <Breadcrumbs arial-label="Breadcrumb" separator={separator} data-testid={testId}>
            {breadcrumbs.map((continuation, index) =>
                continuation(<LinkT extends BreadcrumbElementType>(item: BreadcrumbItem<LinkT>) => {
                    const { label, Link, LinkProps, testId } = item;
                    const Component: ComponentType<typeof LinkProps> = Link;

                    return index < breadcrumbs.length - 1 ? (
                        <Component key={`${label}-link`} className={classes.breadcrumbInactive} data-testid={testId} {...LinkProps}>
                            {label}
                        </Component>
                    ) : (
                        <Typography key={label} className={classes.breadcrumbActive} component="span" data-testid={testId}>
                            {label}
                        </Typography>
                    );
                }),
            )}
        </Breadcrumbs>
    );
};

/** Create a generic breadcrumb where you can set every attribute */
export function breadcrumb<LinkT extends BreadcrumbElementType>(item: BreadcrumbItem<LinkT>): Breadcrumb;
/** Create a generic breadcrumb where you can set every attribute */
export function breadcrumb<LinkT extends BreadcrumbElementType>(
    label: BreadcrumbItem<LinkT>["label"],
    Link: BreadcrumbItem<LinkT>["Link"],
    LinkProps: BreadcrumbItem<LinkT>["LinkProps"],
): Breadcrumb;
/** Create a generic breadcrumb where you can set every attribute */
export function breadcrumb<LinkT extends BreadcrumbElementType>(
    itemOrLabel: BreadcrumbItem<LinkT> | BreadcrumbItem<LinkT>["label"],
    Link?: BreadcrumbItem<LinkT>["Link"],
    LinkProps?: BreadcrumbItem<LinkT>["LinkProps"],
): Breadcrumb {
    if (typeof itemOrLabel === "object") {
        return (breadcrumbCreator) => breadcrumbCreator(itemOrLabel);
    } else {
        return breadcrumb({
            label: itemOrLabel,
            Link: Link as BreadcrumbItem<LinkT>["Link"],
            LinkProps: LinkProps as BreadcrumbItem<LinkT>["LinkProps"],
        });
    }
}

/** Create a breadcrumb using a Material UI `Link` (aka external link) */
export function breadcrumbLink({ href, label }: { href: string; label: string }): Breadcrumb;
/** Create a breadcrumb using a Material UI `Link` (aka external link) */
export function breadcrumbLink(label: string, href: string): Breadcrumb;
/** Create a breadcrumb using a Material UI `Link` (aka external link) */
export function breadcrumbLink(itemOrLabel: { label: string; href: string } | BreadcrumbItem<typeof Link>["label"], href?: string): Breadcrumb {
    if (typeof itemOrLabel === "object") {
        return breadcrumb(itemOrLabel.label, Link, { href: itemOrLabel.href });
    } else {
        return breadcrumb(itemOrLabel, Link, { href: href as string });
    }
}

const useStyles = makeStyles(
    createStyles({
        breadcrumbInactive: {
            fontWeight: "normal",
        },
        breadcrumbActive: {
            color: Color.grey6,
            fontSize: "inherit",
            fontWeight: "bold",
            lineHeight: "inherit",
        },
    }),
);
